/**
 * Placement preset → normalized design state.
 *
 * Turns a `PlacementPreset` (contain box + position) plus the asset's pixel
 * dimensions into the `RelativeDesignState` the rest of the system places with,
 * so a preset-picked placement and a canvas-drawn one are indistinguishable
 * downstream.
 *
 * Flow: preset + asset dimensions + print-area context → `calculatePlacement()`
 * → pixel result → 0-1 normalization.
 *
 * Pure: no Fabric, no DOM, no I/O.
 */

import { calculatePlacement } from './calculator.js';
import type { PlacementPreset, PlacementContext } from './types.js';
import type { RelativeDesignState } from '../design/placementTypes.js';

/**
 * Convert a preset into a normalized placement on the given print area.
 *
 * `PlacementResult.left`/`top` are the design's CENTER in print-area space,
 * which is why they normalize directly into `relCenterX`/`relCenterY`.
 *
 * Note: `PlacementResult.rotationDeg` is dropped — `RelativeDesignState` carries
 * no angle. No shipped preset rotates; a preset that needs to would require a
 * schema change, not a change here.
 */
export function presetToRelativeDesignState(
  preset: PlacementPreset,
  assetWidth: number,
  assetHeight: number,
  ctx: PlacementContext
): RelativeDesignState {
  const result = calculatePlacement(preset, assetWidth, assetHeight, ctx);

  return {
    relCenterX: result.left / ctx.widthPx,
    relCenterY: result.top / ctx.heightPx,
    relWidth: result.scaledWidth / ctx.widthPx,
    designRatio: assetHeight / assetWidth,
  };
}
